import {
  type ChainFullId,
  type ChainId,
  ChainIds,
  type MultichainTokenBalance,
} from "@umami/chains";
import { type UnifiedBalanceWithPrices } from "@umami/state";
import { NATIVE_ETH_ADDRESS, ZEROX_ETH_ADDRESS, getLiFiQuote } from "@umami/swap";

import { type EffectiveOutput, type QuoteProvider, type QuoteWithProvider } from "./types";

export class LiFiProvider implements QuoteProvider {
  canHandleSwap(
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    getNetworkChain: (chainId: ChainFullId) => ChainId
  ): boolean {
    // LiFi supports Ethereum and Etherlink for both same-chain and cross-chain
    const supportedChains = [ChainIds.ETHEREUM, ChainIds.ETHERLINK];

    return (
      supportedChains.includes(getNetworkChain(sellToken.chainId) as "ethereum" | "etherlink") &&
      supportedChains.includes(getNetworkChain(buyToken.chainId) as "ethereum" | "etherlink")
    );
  }

  async fetchQuote(params: {
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    sellAmount: string;
    userAddress: string;
    network: string;
    slippage: number;
    isGaslessSwap?: boolean;
    getNetworkChain: (chainId: ChainFullId) => ChainId;
  }): Promise<QuoteWithProvider | null> {
    const { sellToken, buyToken, sellAmount, userAddress, network, slippage, getNetworkChain } =
      params;

    try {
      const lifiQuote = await getLiFiQuote({
        sellToken: {
          ...sellToken,
          address:
            sellToken.address === ZEROX_ETH_ADDRESS.toLowerCase()
              ? NATIVE_ETH_ADDRESS
              : sellToken.address,
        },
        buyToken: {
          ...buyToken,
          address:
            buyToken.address === ZEROX_ETH_ADDRESS.toLowerCase()
              ? NATIVE_ETH_ADDRESS
              : buyToken.address,
        },
        sellTokenChainId: getNetworkChain(sellToken.chainId),
        buyTokenChainId: getNetworkChain(buyToken.chainId),
        sellAmount,
        userAddress,
        network: network as "mainnet" | "shadownet",
        slippage,
      });

      return {
        provider: "LiFi",
        quote: lifiQuote,
        buyAmount: lifiQuote.buyAmount,
        sellAmount: lifiQuote.sellAmount,
      };
    } catch (error) {
      console.error("Error fetching LiFi quote:", error);
      throw new Error(
        `Failed to fetch LiFi quote: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  getValueAndUnit(
    quote: QuoteWithProvider,
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    getTokenUsdPrice: (symbol: string, amount: number) => number
  ): EffectiveOutput {
    const buyAmountNum = parseFloat(quote.buyAmount);
    const buyAmountUSD = getTokenUsdPrice(buyToken.symbol, buyAmountNum);
    let estimatedGasUSD = 0;

    // LiFi returns gas cost in USD
    const quoteData = quote.quote as any;

    if (quoteData.estimatedGas) {
      estimatedGasUSD = parseFloat(quoteData.estimatedGas);
    }

    if (buyAmountUSD > 0 && estimatedGasUSD > 0) {
      const netUSD = buyAmountUSD - estimatedGasUSD;

      return { value: netUSD, unit: "USD", hasUSD: true };
    }

    return { value: buyAmountNum, unit: buyToken.symbol, hasUSD: false };
  }
}
